import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Dashboard.css";
import api from "../api/client.js";
import { useI18n } from "../i18n/I18nContext.jsx";
import { useAuth } from "../context/AuthContext.jsx";

function formatINR(n) {
  if (n === null || n === undefined || n === "") return "—";
  return "₹" + Number(n).toLocaleString("en-IN");
}

function BarList({ title, data, valueKey, labelKey, colorScheme = "default", onItemClick }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map((d) => d[valueKey]), 1);
  
  const getBarColor = (index) => {
    const colors = {
      default: ["#3498db", "#2980b9", "#5dade2", "#21618c"],
      status: ["#e74c3c", "#f39c12", "#27ae60", "#3498db"],
      department: ["#9b59b6", "#8e44ad", "#c0392b", "#16a085"]
    };
    const palette = colors[colorScheme] || colors.default;
    return palette[index % palette.length];
  };

  return (
    <div className="card card-pad">
      <div className="card-title">
        <i className="ti ti-chart-bar"></i> {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {data.map((d, idx) => {
          const label = d[labelKey] || "Unknown";
          const percentage = ((d[valueKey] / max) * 100).toFixed(0);
          const isClickable = !!onItemClick;
          return (
            <div
              key={label}
              className={`bar-row ${isClickable ? "clickable" : ""}`}
              onClick={() => isClickable && onItemClick(d)}
            >
              <span className="bar-label" title={label.replace(/_/g, " ")}>
                {label.replace(/_/g, " ")}
              </span>
              <div className="bar-track">
                <div
                  className="bar-fill"
                  style={{ 
                    width: `${percentage}%`,
                    background: getBarColor(idx)
                  }}
                ></div>
              </div>
              <span className="bar-val">{d[valueKey]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/dashboard/"),
      api.get("/activity/")
    ]).then(([dashboardRes, activityRes]) => {
      setData(dashboardRes.data);
      const all = activityRes.data.results || activityRes.data;
      setActivities(all.slice(0, 6));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="center-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!data) return <div className="muted">{t("loading")}</div>;

  return (
    <div className="page-dashboard" style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Welcome Banner */}
      <div className="welcome-banner" style={{ marginBottom: 0 }}>
        <div>
          <div className="wb-title">
            {data.scope === "personal" ? `Hi ${user.full_name?.split(" ")[0] || ""} — Your Dashboard`
              : data.scope === "zone" ? `${user.zone_name || "Zone"} Dashboard`
              : data.scope === "department" ? `${user.department || "Department"} Dashboard`
              : "Dashboard Overview"}
          </div>
          <div className="wb-sub">
            {data.scope === "personal" ? "Paras assigned to you, in real time"
              : data.scope === "zone" ? "Paras tracked in your zone, in real time"
              : data.scope === "department" ? "Paras tracked in your department, in real time"
              : "Monitor and manage all audit paras in real time"}
          </div>
        </div>
        <i className="ti ti-file-description wb-icon"></i>
      </div>

      {/* Stat Cards */}
      <div className="stat-row">
        <div className="stat-card clickable" onClick={() => navigate("/paras")} data-testid="total-paras-card">
          <div className="sv">{data.total_paras}</div>
          <div className="sl">{t("totalParas")}</div>
        </div>
        <div className="stat-card red clickable" onClick={() => navigate("/paras?status=PENDING,IN_PROGRESS,REOPENED")} data-testid="pending-paras-card">
          <div className="sv">{data.pending_paras}</div>
          <div className="sl">{t("pendingParas")}</div>
        </div>
        <div className="stat-card green clickable" onClick={() => navigate("/paras?status=RESOLVED,CLOSED")} data-testid="resolved-paras-card">
          <div className="sv">{data.resolved_paras}</div>
          <div className="sl">{t("resolvedParas")}</div>
        </div>

        <div className="stat-card red clickable" onClick={() => navigate("/paras?status=PENDING,IN_PROGRESS,REOPENED&amount_filter=with_amount")} data-testid="amount-pending-card">
          <div className="sv" style={{ fontSize: "16px" }}>
            {formatINR(data.amount_pending)}
          </div>
          <div className="sl">{t("amountPending")}</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="dashboard-charts-grid">
        <BarList 
          title="Paras by Status" 
          data={data.by_status} 
          valueKey="count" 
          labelKey="status"
          colorScheme="status"
          onItemClick={(item) => navigate(`/paras?status=${item.status}`)}
        />
        <BarList 
          title="Paras by Department" 
          data={data.by_department} 
          valueKey="count" 
          labelKey="department"
          colorScheme="department"
          onItemClick={(item) => navigate(`/paras?department=${item.department}`)}
        />
        <BarList 
          title="Paras by Zone" 
          data={data.by_zone} 
          valueKey="count" 
          labelKey="name"
          colorScheme="default"
          onItemClick={(item) => navigate(`/paras?zone=${item.id}`)}
        />
      </div>

      {/* Activity & Quick Actions */}
      <div className="dashboard-activity-grid">
        <div className="card card-pad" style={{ marginBottom: 0 }}>
          <div className="card-title" style={{ marginBottom: "10px" }}>
            <i className="ti ti-history"></i> Recent Activity
          </div>
          {activities.length === 0 ? (
            <div className="muted" style={{ padding: "10px 0" }}>No recent activity.</div>
          ) : (
            <div>
              {activities.map((a, idx) => (
                <div key={a.id} className="activity-item">
                  <div className="activity-dot" style={{ background: "#1e3a8a" }}></div>
                  <div style={{ flex: 1 }}>
                    <span className="act-text" style={{ fontWeight: 600, color: "#0f172a" }}>
                      {a.action.replace(/_/g, " ")}
                    </span>
                    <span className="act-text" style={{ color: "#64748b" }}> by {a.actor_name}</span>
                    <div className="act-meta">
                      {a.detail || "—"} · {new Date(a.created_at).toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <button className="btn-outline" style={{ marginTop: "10px", fontSize: "11.5px" }} onClick={() => navigate("/activity")}>
            View all activity <i className="ti ti-arrow-right" style={{ fontSize: "12px" }}></i>
          </button>
        </div>

        <div className="card card-pad" style={{ alignSelf: "start", marginBottom: 0 }}>
          <div className="card-title" style={{ marginBottom: "10px" }}>
            <i className="ti ti-bolt"></i> Quick Actions
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <button className="btn-primary" style={{ width: "100%", justifyContent: "flex-start", fontSize: "12px", padding: "9px 12px" }} onClick={() => navigate("/paras")} data-testid="view-paras-btn">
              <i className="ti ti-file-description" style={{ fontSize: "14px" }}></i> View All Paras
            </button>
            {["SUPER_ADMIN", "CORP_ADMIN", "AUDITOR", "ZONAL_AC"].includes(user.role) && (
              <button className="btn-outline" style={{ width: "100%", justifyContent: "flex-start", fontSize: "12px", padding: "8px 12px", color: "#172554", borderColor: "#bfdbfe" }} onClick={() => navigate("/officers")} data-testid="manage-officers-btn">
                <i className="ti ti-users" style={{ fontSize: "14px", color: "#1e3a8a" }}></i> Manage Officers
              </button>
            )}
            {["SUPER_ADMIN", "CORP_ADMIN", "AUDITOR"].includes(user.role) && (
              <button className="btn-outline" style={{ width: "100%", justifyContent: "flex-start", fontSize: "12px", padding: "8px 12px", color: "#172554", borderColor: "#bfdbfe" }} onClick={() => navigate("/zones")} data-testid="manage-zones-btn">
                <i className="ti ti-map-pin" style={{ fontSize: "14px", color: "#1e3a8a" }}></i> Manage Zones
              </button>
            )}
            <button className="btn-outline" style={{ width: "100%", justifyContent: "flex-start", fontSize: "12px", padding: "8px 12px", color: "#172554", borderColor: "#bfdbfe" }} onClick={() => navigate("/notifications")} data-testid="notifications-btn">
              <i className="ti ti-bell" style={{ fontSize: "14px", color: "#1e3a8a" }}></i> Notifications
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
